/**
 * 直链 HTML5 播放器（视频链接 / 私有云 vodPlatform=10）
 * API 对齐 player/polyv.js
 *
 * compliance=true：禁倍速；已学区间内可自由拖动，未学区域不可拖；1/3&2/3 checkpoint 钩子
 * 播放地址：mp4 走同域 /media-proxy，绕过 CDN Referer 防盗链
 * m3u8（课通云 HLS）不走代理，Safari 原生 / Chrome 用 hls.js
 *
 * hls.js 必须打进业务包：勿依赖 jsDelivr（部分网络/Edge 环境拉不到会黑屏）
 */

class UrlPlayer {
  video = null
  el = null
  event = {}
  playStatus = false
  #hls = null
  #timeupdateTimer = null
  #banSeek = false
  #banRate = false
  #compliance = false
  /** 已学最大落点（秒）：进度条仅允许拖到此范围内 */
  #maxSeek = 0
  #isSeekingUi = false
  /** 断点续播定位中：避免 timeupdate 在跳转完成前干扰 */
  #resumePending = false
  #resumeAt = 0
  #resumeTimer = null
  #resumeTries = 0
  #resumeDone = false
  /** 业务主动 pause 后禁止异常拉起 */
  #holdPause = false
  #checkpoints = []
  #firedCheckpoints = new Set()
  #rateGuardTimer = null
  #controlsGuardTimer = null
  #controlsObserver = null
  #seekSettleTimer = null
  #ui = null
  #onVisibility = null
  #onWindowBlur = null
  #leaveScreenNotified = false
  #wrapBaseHeight = ''
  #fsGuard = null
  /** 容器全屏切换瞬间可能触发 blur，短暂忽略切屏暂停 */
  #fsSwitching = false
  /** 移动端 CSS 横屏全屏兜底 */
  #cssLandscapeFs = false
  #mobileFsStyles = null
  #onFsViewportChange = null
  #fsParent = null
  #fsNextSibling = null
  #videoShield = null

  constructor (param = {}, time = 3000) {
    const wrap = typeof param.wrap === 'string'
      ? document.querySelector(param.wrap)
      : param.wrap
    if (!wrap) {
      throw new Error('UrlPlayer: wrap 容器不存在')
    }
    if (!param.url) {
      throw new Error('UrlPlayer: 缺少播放地址 url')
    }

    this.#compliance = param.compliance === true
    this.#banSeek = this.#compliance || param.ban_seek === 'on' || param.ban_seek === true
    this.#banRate = this.#compliance || param.ban_rate === true
    this.#checkpoints = Array.isArray(param.checkpoints) && param.checkpoints.length
      ? param.checkpoints.map(Number)
      : (this.#compliance ? [1 / 3, 2 / 3] : [])
    const resumeAt = Number(param.watchStartTime) || 0
    this.#resumeAt = resumeAt
    this.#maxSeek = Math.max(Number(param.maxPositionSec) || 0, resumeAt)
    this.#resumePending = resumeAt > 0.5

    wrap.innerHTML = ''
    wrap.style.position = 'relative'
    wrap.style.background = '#000'
    this.#wrapBaseHeight = wrap.style.height || ''

    const video = document.createElement('video')
    video.setAttribute('playsinline', 'true')
    video.setAttribute('webkit-playsinline', 'true')
    video.setAttribute('x5-playsinline', 'true')
    video.setAttribute('x5-video-player-type', 'h5')
    // false：避免 X5/华为等拉起带倍速的原生全屏页
    video.setAttribute('x5-video-player-fullscreen', 'false')
    video.setAttribute('x5-video-orientation', 'portrait')
    video.setAttribute('referrerpolicy', 'no-referrer')
    video.setAttribute(
      'controlslist',
      this.#compliance
        ? 'nodownload noplaybackrate noremoteplayback nofullscreen'
        : 'nodownload'
    )
    video.setAttribute('disablePictureInPicture', 'true')
    video.controls = !this.#compliance
    video.playsInline = true
    try {
      if (this.#compliance) video.disableRemotePlayback = true
    } catch (e) {}
    video.preload = 'auto'
    video.style.display = 'block'
    video.style.width = '100%'
    video.style.height = this.#compliance ? 'calc(100% - 44px)' : '100%'
    video.style.background = '#000'
    video.style.objectFit = 'contain'
    video.style.objectPosition = 'center center'
    video.playbackRate = 1
    if (param.autoplay) {
      video.autoplay = true
    }
    this.#bindMediaSource(video, param.url)
    wrap.appendChild(video)

    if (this.#compliance) {
      this.#buildComplianceUi(wrap, video)
      this.#lockNativeControls(wrap, video)
      this.#bindNativeFullscreenGuard(wrap, video)
      this.#bindLeaveScreenGuard()
    }

    this.el = wrap
    this.video = video
    this.#bindResume(video)

    if (this.#banSeek) {
      // 仅拦截「跳到未学区域」；不要把原生 seeking/seeked 当成 UI 拖拽，
      // 否则缓冲/续播会反复拉高 isSeekingUi，导致 maxSeek 冻住、进度上报中断。
      video.addEventListener('seeking', () => {
        if (this.#isSeekingUi || this.#resumePending) return
        const max = Math.max(this.#maxSeek, 0)
        if (video.currentTime > max + 0.35) {
          try {
            video.currentTime = max
          } catch (e) {}
          this.#emit('seek_blocked', max)
        }
      })
    }

    video.addEventListener('timeupdate', () => {
      // 正常播放：持续抬高已学边界；UI 拖拽 / 续播定位中不抬
      if (!this.#resumePending && !this.#isSeekingUi && !video.seeking && video.currentTime > this.#maxSeek) {
        this.#maxSeek = video.currentTime
      }
      this.#updateUiProgress()
      this.#checkCheckpoints()
    })

    if (this.#banRate) {
      this.#rateGuardTimer = setInterval(() => {
        if (video.playbackRate !== 1) {
          video.playbackRate = 1
        }
      }, 300)
      video.addEventListener('ratechange', () => {
        if (video.playbackRate !== 1) {
          video.playbackRate = 1
        }
      })
    }

    // 合规上报由业务侧节流；定时器保持约 1s 以便进度条刷新
    this.#timeupdateTimer = setInterval(() => {
      const cbs = this.event.timeupdate
      if (cbs && cbs.length) {
        const t = this.getCurrentTime()
        const d = this.getDuration()
        cbs.forEach((fn) => fn && fn(t, d))
      }
    }, this.#compliance ? Math.max(Math.min(time, 1000), 500) : time)

    video.addEventListener('ended', () => {
      // 拖拽中 / 已学未近片尾：不上抛 ended（防拖到片尾误完课）
      if (this.#isSeekingUi || video.seeking) return
      const dur = Number(video.duration) || 0
      if (dur > 0 && this.#maxSeek + 5 < dur && (this.#maxSeek / dur) * 100 < 95) return
      clearInterval(this.#timeupdateTimer)
      this.#emit('ended')
    })
    video.addEventListener('play', () => {
      this.playStatus = true
      this.#emit('play')
      this.#syncPlayButton()
    })
    video.addEventListener('pause', () => {
      this.playStatus = false
      this.#emit('pause')
      this.#syncPlayButton()
    })
    video.addEventListener('loadedmetadata', () => {
      this.#emit('ready')
      this.#updateUiProgress()
    })
    video.addEventListener('error', () => {
      this.#emit('error', video.error)
    })

    if (video.readyState >= 1) {
      setTimeout(() => this.#emit('ready'), 0)
    }
  }

  #bindLeaveScreenGuard () {
    this.#onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        this.#pauseForLeaveScreen()
      } else {
        this.#leaveScreenNotified = false
      }
    }
    this.#onWindowBlur = () => {
      // 切到其它窗口/应用：必须停在本页主屏观看
      this.#pauseForLeaveScreen()
    }
    document.addEventListener('visibilitychange', this.#onVisibility)
    window.addEventListener('blur', this.#onWindowBlur)
  }

  /**
   * 锁死原生控件：Edge/Chrome 可通过右键「显示所有控件」打开原生进度条并拖拽。
   */
  #lockNativeControls (wrap, video) {
    const forceOff = () => {
      try {
        if (video.controls) video.controls = false
        if (video.hasAttribute('controls')) video.removeAttribute('controls')
      } catch (e) {}
    }
    forceOff()
    video.setAttribute('controlslist', 'nodownload noplaybackrate noremoteplayback nofullscreen')
    const blockMenu = (e) => {
      e.preventDefault()
      e.stopPropagation()
      forceOff()
      return false
    }
    video.addEventListener('contextmenu', blockMenu)
    if (wrap) wrap.addEventListener('contextmenu', blockMenu)
    this.#controlsObserver = new MutationObserver(forceOff)
    this.#controlsObserver.observe(video, { attributes: true, attributeFilter: ['controls'] })
    this.#controlsGuardTimer = setInterval(forceOff, 400)
  }

  /**
   * 拦截 video 原生全屏（APP/H5 系统条可随意拖与倍速），改用容器全屏保留合规 UI。
   */
  #bindNativeFullscreenGuard (wrap, video) {
    const getFsEl = () =>
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement ||
      null

    const exitDocFs = () => {
      const exit =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.mozCancelFullScreen ||
        document.msExitFullscreen
      if (exit) {
        try {
          const p = exit.call(document)
          if (p && typeof p.catch === 'function') p.catch(() => {})
        } catch (e) {}
      }
    }

    const exitNativeVideoFs = () => {
      try {
        if (typeof video.webkitExitFullscreen === 'function') video.webkitExitFullscreen()
      } catch (e) {}
      if (getFsEl() === video) exitDocFs()
    }

    const markFsSwitching = () => {
      this.#fsSwitching = true
      setTimeout(() => {
        this.#fsSwitching = false
      }, 1200)
    }

    const enterContainerFs = () => {
      markFsSwitching()
      if (this.#isMobileEnv()) {
        this.#enterCssLandscapeFs(wrap)
        if (this.#isAppRuntime()) this.#lockLandscape()
        applyLayout()
        return true
      }
      const req =
        wrap.requestFullscreen ||
        wrap.webkitRequestFullscreen ||
        wrap.mozRequestFullScreen ||
        wrap.msRequestFullscreen
      if (!req) {
        this.#enterCssLandscapeFs(wrap)
        applyLayout()
        return true
      }
      try {
        const p = req.call(wrap)
        if (p && typeof p.then === 'function') {
          p.then(() => applyLayout()).catch(() => {
            this.#enterCssLandscapeFs(wrap)
            applyLayout()
          })
        } else {
          applyLayout()
        }
        return true
      } catch (e) {
        this.#enterCssLandscapeFs(wrap)
        applyLayout()
        return true
      }
    }

    const exitContainerFs = () => {
      markFsSwitching()
      this.#unlockOrientation()
      if (this.#cssLandscapeFs) {
        this.#exitCssLandscapeFs(wrap)
      }
      if (getFsEl() === wrap) exitDocFs()
      applyLayout()
    }

    const applyLayout = () => {
      const inFs = this.#isInFullscreen()
      if (inFs) {
        if (!this.#cssLandscapeFs) {
          wrap.style.width = '100%'
          wrap.style.height = '100%'
          wrap.style.maxHeight = '100%'
        }
        this.#layoutFsMedia(true)
      } else if (!this.#cssLandscapeFs) {
        if (this.#wrapBaseHeight) wrap.style.height = this.#wrapBaseHeight
        wrap.style.width = ''
        wrap.style.maxHeight = ''
        this.#layoutFsMedia(false)
      }
      if (this.#ui && this.#ui.fsBtn) {
        this.#ui.fsBtn.textContent = inFs ? '退出' : '全屏'
      }
    }

    const abandonCssFsForBrowser = () => {
      if (this.#isAppRuntime()) return false
      if (!this.#cssLandscapeFs) return false
      markFsSwitching()
      this.#exitCssLandscapeFs(wrap)
      applyLayout()
      return true
    }

    const blockNativeAndRedirect = () => {
      markFsSwitching()
      exitNativeVideoFs()
      setTimeout(() => {
        if (abandonCssFsForBrowser()) {
          this.#emit('native_fullscreen_blocked')
          return
        }
        if (!this.#cssLandscapeFs) {
          enterContainerFs()
        } else {
          this.#applyCssLandscapeLayout(wrap)
        }
        applyLayout()
        this.#emit('native_fullscreen_blocked')
      }, 0)
    }

    const onWebkitBegin = () => {
      try {
        if (typeof video.webkitExitFullscreen === 'function') video.webkitExitFullscreen()
      } catch (e) {}
      blockNativeAndRedirect()
    }

    const onWebkitEnd = () => {
      if (abandonCssFsForBrowser()) return
      if (this.#cssLandscapeFs || this.#fsSwitching) {
        setTimeout(() => {
          if (this.#cssLandscapeFs && this.el) this.#applyCssLandscapeLayout(this.el)
        }, 50)
      }
    }

    const onFsChange = () => {
      const fsEl = getFsEl()
      if (fsEl === video) {
        blockNativeAndRedirect()
        return
      }
      if (!this.#isAppRuntime() && this.#isMobileEnv() && fsEl) {
        exitDocFs()
        abandonCssFsForBrowser()
        return
      }
      if (this.#isMobileEnv() && fsEl === wrap && this.#cssLandscapeFs && !this.#fsSwitching) {
        exitDocFs()
        setTimeout(() => this.#applyCssLandscapeLayout(wrap), 0)
        return
      }
      if (!fsEl && !this.#isAppRuntime()) {
        abandonCssFsForBrowser()
        return
      }
      applyLayout()
    }

    const onPageShow = () => {
      if (!this.#isAppRuntime() && this.#cssLandscapeFs) {
        setTimeout(() => abandonCssFsForBrowser(), 0)
      }
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') onPageShow()
    }

    video.addEventListener('webkitbeginfullscreen', onWebkitBegin)
    video.addEventListener('webkitendfullscreen', onWebkitEnd)
    document.addEventListener('fullscreenchange', onFsChange)
    document.addEventListener('webkitfullscreenchange', onFsChange)
    document.addEventListener('mozfullscreenchange', onFsChange)
    document.addEventListener('MSFullscreenChange', onFsChange)
    window.addEventListener('pageshow', onPageShow)
    document.addEventListener('visibilitychange', onVisibility)

    try {
      if (typeof video.webkitEnterFullscreen === 'function') {
        video.webkitEnterFullscreen = () => {
          if (!this.#isAppRuntime()) return
          enterContainerFs()
        }
      }
    } catch (e) {}
    try {
      video.requestFullscreen = () => {
        if (!this.#isAppRuntime() && this.#isMobileEnv()) {
          return Promise.resolve()
        }
        enterContainerFs()
        return Promise.resolve()
      }
    } catch (e) {}

    this.#fsGuard = {
      onWebkitBegin,
      onWebkitEnd,
      onFsChange,
      onPageShow,
      onVisibility,
      enterContainerFs,
      exitContainerFs,
      getFsEl,
      wrap,
      video
    }
  }

  #isMobileEnv () {
    try {
      if (typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches) {
        return true
      }
      if (navigator.maxTouchPoints > 0) return true
      return /Android|iPhone|iPad|iPod|Mobile|HarmonyOS/i.test(navigator.userAgent || '')
    } catch (e) {
      return false
    }
  }

  #isAppRuntime () {
    try {
      if (typeof window !== 'undefined' && window.plus) return true
      const ua = navigator.userAgent || ''
      if (/Html5Plus|uni-app/i.test(ua)) return true
    } catch (e) {}
    return false
  }

  #shouldForceLandscapeCss () {
    return this.#isAppRuntime()
  }

  #isInFullscreen () {
    if (this.#cssLandscapeFs) return true
    try {
      const fs =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      return !!(fs && this.el && (fs === this.el || (this.el.contains && this.el.contains(fs))))
    } catch (e) {
      return false
    }
  }

  isContainerFullscreen () {
    return this.#isInFullscreen() || this.#fsSwitching
  }

  #lockLandscape () {
    try {
      const o = screen.orientation
      if (o && typeof o.lock === 'function') {
        const p = o.lock('landscape')
        if (p && typeof p.catch === 'function') p.catch(() => {})
      }
    } catch (e) {}
  }

  #unlockOrientation () {
    try {
      const o = screen.orientation
      if (o && typeof o.unlock === 'function') o.unlock()
    } catch (e) {}
  }

  #reparentWrapToBody (wrap) {
    if (!wrap || !wrap.parentNode) return
    if (wrap.parentNode === document.body) return
    this.#fsParent = wrap.parentNode
    this.#fsNextSibling = wrap.nextSibling
    document.body.appendChild(wrap)
  }

  #restoreWrapParent (wrap) {
    if (!wrap || !this.#fsParent) return
    try {
      if (this.#fsNextSibling && this.#fsNextSibling.parentNode === this.#fsParent) {
        this.#fsParent.insertBefore(wrap, this.#fsNextSibling)
      } else {
        this.#fsParent.appendChild(wrap)
      }
    } catch (e) {
      try {
        this.#fsParent.appendChild(wrap)
      } catch (e2) {}
    }
    this.#fsParent = null
    this.#fsNextSibling = null
  }

  #layoutFsMedia (inFs) {
    const video = this.video
    if (!video) return
    if (inFs) {
      video.style.position = 'absolute'
      video.style.left = '0'
      video.style.top = '0'
      video.style.right = '0'
      video.style.width = '100%'
      video.style.height = 'calc(100% - 44px)'
      video.style.maxWidth = '100%'
      video.style.maxHeight = 'calc(100% - 44px)'
      video.style.objectFit = 'contain'
      video.style.objectPosition = 'center center'
      this.#ensureVideoShield()
    } else {
      video.style.position = ''
      video.style.left = ''
      video.style.top = ''
      video.style.right = ''
      video.style.width = '100%'
      video.style.height = 'calc(100% - 44px)'
      video.style.maxWidth = ''
      video.style.maxHeight = ''
      this.#removeVideoShield()
    }
  }

  #ensureVideoShield () {
    if (!this.el || this.#videoShield) return
    const shield = document.createElement('div')
    shield.setAttribute('data-train-video-shield', '1')
    shield.style.cssText =
      'position:absolute;left:0;top:0;right:0;bottom:44px;z-index:4;background:transparent;-webkit-tap-highlight-color:transparent;touch-action:none;user-select:none;-webkit-user-select:none;'
    const block = (e) => {
      e.preventDefault()
      e.stopPropagation()
    }
    ;['contextmenu', 'dblclick', 'gesturestart', 'gesturechange', 'gestureend'].forEach((type) => {
      shield.addEventListener(type, block, { passive: false })
    })
    shield.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches && e.touches.length > 1) block(e)
      },
      { passive: false }
    )
    shield.addEventListener(
      'touchmove',
      (e) => {
        block(e)
      },
      { passive: false }
    )
    let longPressTimer = null
    shield.addEventListener(
      'touchstart',
      (e) => {
        if (longPressTimer) clearTimeout(longPressTimer)
        longPressTimer = setTimeout(() => {
          longPressTimer = null
          block(e)
        }, 280)
      },
      { passive: true }
    )
    const clearLongPress = () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer)
        longPressTimer = null
      }
    }
    shield.addEventListener('touchend', clearLongPress)
    shield.addEventListener('touchcancel', clearLongPress)
    shield.addEventListener('click', block)
    this.el.appendChild(shield)
    this.#videoShield = shield
  }

  #removeVideoShield () {
    if (this.#videoShield) {
      try {
        this.#videoShield.remove()
      } catch (e) {}
      this.#videoShield = null
    }
  }

  #enterCssLandscapeFs (wrap) {
    if (!wrap) return
    if (!this.#mobileFsStyles) {
      this.#mobileFsStyles = {
        position: wrap.style.position,
        top: wrap.style.top,
        left: wrap.style.left,
        right: wrap.style.right,
        bottom: wrap.style.bottom,
        width: wrap.style.width,
        height: wrap.style.height,
        maxHeight: wrap.style.maxHeight,
        maxWidth: wrap.style.maxWidth,
        zIndex: wrap.style.zIndex,
        transform: wrap.style.transform,
        transformOrigin: wrap.style.transformOrigin,
        background: wrap.style.background,
        overflow: wrap.style.overflow,
        margin: wrap.style.margin,
        padding: wrap.style.padding,
        boxSizing: wrap.style.boxSizing,
        bodyOverflow: document.body ? document.body.style.overflow : ''
      }
    }
    this.#cssLandscapeFs = true
    this.#reparentWrapToBody(wrap)
    this.#applyCssLandscapeLayout(wrap)
    if (!this.#onFsViewportChange) {
      this.#onFsViewportChange = () => {
        if (this.#cssLandscapeFs && this.el) {
          this.#applyCssLandscapeLayout(this.el)
        }
      }
      window.addEventListener('resize', this.#onFsViewportChange)
      window.addEventListener('orientationchange', this.#onFsViewportChange)
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.#onFsViewportChange)
        window.visualViewport.addEventListener('scroll', this.#onFsViewportChange)
      }
    }
    if (this.#ui && this.#ui.fsBtn) {
      this.#ui.fsBtn.textContent = '退出'
    }
  }

  #viewportSize () {
    try {
      const vv = window.visualViewport
      if (vv && vv.width && vv.height) {
        return {
          vw: Math.round(vv.width),
          vh: Math.round(vv.height),
          ox: Math.round(vv.offsetLeft || 0),
          oy: Math.round(vv.offsetTop || 0)
        }
      }
    } catch (e) {}
    return {
      vw: Math.round(window.innerWidth || document.documentElement.clientWidth || 0),
      vh: Math.round(window.innerHeight || document.documentElement.clientHeight || 0),
      ox: 0,
      oy: 0
    }
  }

  #applyCssLandscapeLayout (wrap) {
    const { vw, vh, ox, oy } = this.#viewportSize()
    if (!vw || !vh) return
    wrap.style.position = 'fixed'
    wrap.style.zIndex = '2147483000'
    wrap.style.background = '#000'
    wrap.style.overflow = 'hidden'
    wrap.style.margin = '0'
    wrap.style.padding = '0'
    wrap.style.boxSizing = 'border-box'
    wrap.style.right = 'auto'
    wrap.style.bottom = 'auto'
    wrap.style.maxWidth = 'none'
    wrap.style.maxHeight = 'none'
    if (document.body) document.body.style.overflow = 'hidden'
    if (this.#shouldForceLandscapeCss() && vh > vw) {
      wrap.style.width = `${vh}px`
      wrap.style.height = `${vw}px`
      wrap.style.top = `${oy + (vh - vw) / 2}px`
      wrap.style.left = `${ox + (vw - vh) / 2}px`
      wrap.style.transform = 'rotate(90deg)'
      wrap.style.transformOrigin = 'center center'
    } else {
      wrap.style.top = `${oy}px`
      wrap.style.left = `${ox}px`
      wrap.style.width = `${vw}px`
      wrap.style.height = `${vh}px`
      wrap.style.transform = 'none'
      wrap.style.transformOrigin = 'center center'
    }
    this.#layoutFsMedia(true)
  }

  #exitCssLandscapeFs (wrap) {
    this.#cssLandscapeFs = false
    if (this.#onFsViewportChange) {
      window.removeEventListener('resize', this.#onFsViewportChange)
      window.removeEventListener('orientationchange', this.#onFsViewportChange)
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.#onFsViewportChange)
        window.visualViewport.removeEventListener('scroll', this.#onFsViewportChange)
      }
      this.#onFsViewportChange = null
    }
    const s = this.#mobileFsStyles
    if (wrap && s) {
      wrap.style.position = s.position || 'relative'
      wrap.style.top = s.top || ''
      wrap.style.left = s.left || ''
      wrap.style.right = s.right || ''
      wrap.style.bottom = s.bottom || ''
      wrap.style.width = s.width || ''
      wrap.style.height = s.height || this.#wrapBaseHeight || ''
      wrap.style.maxHeight = s.maxHeight || ''
      wrap.style.maxWidth = s.maxWidth || ''
      wrap.style.zIndex = s.zIndex || ''
      wrap.style.transform = s.transform || ''
      wrap.style.transformOrigin = s.transformOrigin || ''
      wrap.style.background = s.background || '#000'
      wrap.style.overflow = s.overflow || ''
      wrap.style.margin = s.margin || ''
      wrap.style.padding = s.padding || ''
      wrap.style.boxSizing = s.boxSizing || ''
      if (document.body) document.body.style.overflow = s.bodyOverflow || ''
      this.#mobileFsStyles = null
    }
    this.#restoreWrapParent(wrap)
    this.#layoutFsMedia(false)
    if (this.#ui && this.#ui.fsBtn) {
      this.#ui.fsBtn.textContent = '全屏'
    }
  }

  #toggleContainerFullscreen () {
    if (!this.#fsGuard) return
    this.#fsSwitching = true
    setTimeout(() => {
      this.#fsSwitching = false
    }, 1200)
    if (this.#isInFullscreen()) {
      this.#fsGuard.exitContainerFs()
    } else {
      this.#fsGuard.enterContainerFs()
    }
  }

  /** 业务侧退出全屏（播完进考等）：兼容容器全屏 / CSS 伪全屏 / 原生 video 全屏 */
  exitFullscreen () {
    try {
      if (this.#fsGuard && typeof this.#fsGuard.exitContainerFs === 'function') {
        this.#fsGuard.exitContainerFs()
      }
    } catch (e) {}
    try {
      if (this.video && typeof this.video.webkitExitFullscreen === 'function') {
        this.video.webkitExitFullscreen()
      }
    } catch (e) {}
    try {
      const exit =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.mozCancelFullScreen ||
        document.msExitFullscreen
      if (exit && (document.fullscreenElement || document.webkitFullscreenElement)) {
        const p = exit.call(document)
        if (p && typeof p.catch === 'function') p.catch(() => {})
      }
    } catch (e) {}
  }

  /** 兼容 sign.vue 等旧调用名 */
  exitFullScreen () {
    return this.exitFullscreen()
  }

  #pauseForLeaveScreen () {
    if (!this.video) return
    if (this.#fsSwitching || this.#isInFullscreen()) return
    // 容器全屏中：fullscreen 可能伴随 blur，勿当切屏
    try {
      const fs =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      if (fs && this.el && (fs === this.el || (this.el.contains && this.el.contains(fs)))) {
        return
      }
    } catch (e) {}
    // 仅「正在播放」时切屏才暂停并提示；目录页/已暂停不弹提示
    const wasPlaying = this.playStatus && !this.video.paused
    if (!wasPlaying) {
      return
    }
    this.pause()
    if (!this.#leaveScreenNotified) {
      this.#leaveScreenNotified = true
      this.#emit('leave_screen')
    }
  }

  #buildComplianceUi (wrap, video) {
    const bar = document.createElement('div')
    bar.style.cssText = 'position:absolute;left:0;right:0;bottom:0;height:44px;display:flex;align-items:center;gap:10px;padding:0 12px;background:rgba(0,0,0,.72);color:#fff;font-size:12px;z-index:6;user-select:none;'
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.textContent = '播放'
    btn.style.cssText = 'border:0;background:#409eff;color:#fff;border-radius:4px;padding:4px 10px;cursor:pointer;'
    btn.addEventListener('click', () => {
      if (video.paused) this.play()
      else this.pause()
    })
    const time = document.createElement('span')
    time.textContent = '00:00 / 00:00'
    const track = document.createElement('div')
    track.style.cssText = 'flex:1;height:6px;background:rgba(255,255,255,.25);border-radius:3px;overflow:hidden;position:relative;cursor:pointer;'
    const learned = document.createElement('div')
    learned.style.cssText = 'position:absolute;left:0;top:0;height:100%;width:0;background:rgba(64,158,255,.35);pointer-events:none;'
    const fill = document.createElement('div')
    fill.style.cssText = 'position:absolute;left:0;top:0;height:100%;width:0;background:#409eff;pointer-events:none;'
    track.appendChild(learned)
    track.appendChild(fill)
    const tip = document.createElement('span')
    tip.textContent = '已学可拖·禁倍速'
    tip.style.cssText = 'opacity:.75;white-space:nowrap;'
    const fsBtn = document.createElement('button')
    fsBtn.type = 'button'
    fsBtn.textContent = '全屏'
    fsBtn.style.cssText = 'border:0;background:rgba(255,255,255,.18);color:#fff;border-radius:4px;padding:4px 8px;cursor:pointer;flex-shrink:0;'
    fsBtn.addEventListener('click', (e) => {
      e.stopPropagation()
      this.#toggleContainerFullscreen()
    })
    bar.appendChild(btn)
    bar.appendChild(time)
    bar.appendChild(track)
    bar.appendChild(tip)
    bar.appendChild(fsBtn)
    wrap.appendChild(bar)

    const seekFromClientPos = (clientX, clientY) => {
      const rect = track.getBoundingClientRect()
      let ratio
      const { vw, vh } = this.#viewportSize()
      if (this.#cssLandscapeFs && this.#shouldForceLandscapeCss() && vh > vw) {
        if (!rect.height) return
        ratio = Math.min(1, Math.max(0, (clientY - rect.top) / rect.height))
      } else {
        if (!rect.width) return
        ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
      }
      const dur = this.getDuration()
      if (!dur) return
      const target = ratio * dur
      const max = Math.max(this.#maxSeek, 0)
      this.#markSeekingUi(true)
      if (target > max + 0.25) {
        this.seek(max)
        this.#emit('seek_blocked', max)
      } else {
        this.seek(target)
      }
      this.#updateUiProgress()
    }
    let dragging = false
    track.addEventListener('click', (e) => {
      seekFromClientPos(e.clientX, e.clientY)
      this.#markSeekingUi(true, 600)
    })
    track.addEventListener('mousedown', (e) => {
      dragging = true
      this.#markSeekingUi(true)
      seekFromClientPos(e.clientX, e.clientY)
      e.preventDefault()
    })
    const onMove = (e) => {
      if (!dragging) return
      seekFromClientPos(e.clientX, e.clientY)
    }
    const onUp = () => {
      if (!dragging) return
      dragging = false
      this.#markSeekingUi(true, 600)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    this.#ui = { btn, time, fill, learned, tip, fsBtn, onMove, onUp }
  }

  /** 标记 UI 拖拽中；settleMs>0 时延迟解除，覆盖上报定时器竞态 */
  #markSeekingUi (on, settleMs = 0) {
    if (this.#seekSettleTimer) {
      clearTimeout(this.#seekSettleTimer)
      this.#seekSettleTimer = null
    }
    if (on) {
      this.#isSeekingUi = true
      if (settleMs > 0) {
        this.#seekSettleTimer = setTimeout(() => {
          this.#isSeekingUi = false
          this.#seekSettleTimer = null
        }, settleMs)
      }
    } else {
      this.#isSeekingUi = false
    }
  }

  isSeekingUi () {
    return !!(this.#isSeekingUi || (this.video && this.video.seeking))
  }

  /** 上报用落点：UI 拖拽中用已学 max；正常播放用 playhead 与 max 的较大值 */
  getReportPositionSec () {
    const max = Math.max(this.#maxSeek, 0)
    if (this.#isSeekingUi || (this.video && this.video.seeking)) return Math.floor(max)
    const cur = Math.floor(this.getCurrentTime())
    return Math.max(cur, Math.floor(max))
  }

  #syncPlayButton () {
    if (this.#ui && this.#ui.btn) {
      this.#ui.btn.textContent = this.playStatus ? '暂停' : '播放'
    }
  }

  #updateUiProgress () {
    if (!this.#ui) return
    const cur = this.getCurrentTime()
    const dur = this.getDuration()
    this.#ui.time.textContent = `${this.#fmt(cur)} / ${this.#fmt(dur)}`
    const pct = dur > 0 ? Math.min(100, (cur / dur) * 100) : 0
    this.#ui.fill.style.width = `${pct}%`
    if (this.#ui.learned) {
      const learnedPct = dur > 0 ? Math.min(100, (Math.max(this.#maxSeek, 0) / dur) * 100) : 0
      this.#ui.learned.style.width = `${learnedPct}%`
    }
  }

  #fmt (sec) {
    const s = Math.max(0, Math.floor(sec || 0))
    const m = Math.floor(s / 60)
    const r = s % 60
    return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`
  }

  #checkCheckpoints () {
    const dur = this.getDuration()
    if (!dur || !this.#checkpoints.length) return
    const cur = this.getCurrentTime()
    this.#checkpoints.forEach((ratio, idx) => {
      const n = idx + 1
      if (this.#firedCheckpoints.has(n)) return
      if (cur >= dur * ratio) {
        this.#firedCheckpoints.add(n)
        this.#emit('checkpoint', n, cur)
      }
    })
  }

  #toProxyUrl (url) {
    if (!url) return url
    if (String(url).startsWith('/media-proxy')) return url
    if (!/^https?:\/\//i.test(url)) return url
    return `/media-proxy?url=${encodeURIComponent(url)}`
  }

  /**
   * 仅 Safari/iOS 走原生 HLS；Chrome/Edge 的 canPlayType 可能误报支持，
   * 却播不了课通云加密流，会导致连 hls.js 都不加载、画面黑屏。
   */
  #canUseNativeHls (video) {
    if (!video || !video.canPlayType) return false
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || ''
    const isApple =
      /iPhone|iPad|iPod/i.test(ua) ||
      (/Macintosh/i.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document) ||
      /^((?!chrome|android|edg).)*safari/i.test(ua)
    if (!isApple) return false
    return !!video.canPlayType('application/vnd.apple.mpegurl')
  }

  /**
   * mp4 直链走 media-proxy；m3u8 走原生 HLS 或 hls.js（playlist 分片不宜再套代理）
   */
  #bindMediaSource (video, url) {
    if (!video || !url) return
    const isHls = /\.m3u8(\?|$)/i.test(String(url))
    if (!isHls) {
      video.src = this.#toProxyUrl(url)
      return
    }
    if (this.#canUseNativeHls(video)) {
      video.src = url
      return
    }
    this.#loadHls().then((Hls) => {
      if (!Hls || !Hls.isSupported()) {
        video.src = url
        return
      }
      this.#destroyHls()
      const hlsOpts = { enableWorker: true, maxBufferLength: 30 }
      if (this.#resumeAt > 0.5) {
        hlsOpts.startPosition = this.#resumeAt
      }
      this.#hls = new Hls(hlsOpts)
      this.#hls.loadSource(url)
      this.#hls.attachMedia(video)
      this.#hls.on(Hls.Events.MANIFEST_PARSED, () => {
        this.#tryApplyResume()
      })
      this.#hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (data && data.fatal) {
          this.#emit('error', data)
        }
      })
    }).catch(() => {
      video.src = url
    })
  }

  #bindResume (video) {
    if (!(this.#resumeAt > 0.5) || !video) return
    this.#resumePending = true
    this.#resumeTries = 0
    this.#resumeDone = false
    const kick = () => this.#tryApplyResume()
    video.addEventListener('loadedmetadata', kick, { once: true })
    video.addEventListener('loadeddata', kick, { once: true })
    video.addEventListener('canplay', kick, { once: true })
    video.addEventListener('seeked', () => {
      if (this.#resumeDone || !this.#resumePending) return
      const cur = Number(video.currentTime) || 0
      const target = this.#resumeTarget()
      if (cur >= target - 1.5) {
        this.#clearResumePending()
      }
    })
    if (video.readyState >= 1) kick()
  }

  #resumeTarget () {
    const target = Math.max(0, Number(this.#resumeAt) || 0)
    return Math.min(target, Math.max(this.#maxSeek, target))
  }

  #tryApplyResume () {
    if (this.#resumeDone) return
    const video = this.video
    const target = this.#resumeTarget()
    if (!video || !(target > 0.5)) {
      this.#clearResumePending()
      return
    }
    let t = target
    const dur = Number(video.duration)
    if (Number.isFinite(dur) && dur > 1) {
      t = Math.min(target, Math.max(0, dur - 0.3))
    }
    const cur = Number(video.currentTime) || 0
    if (cur >= t - 1.5) {
      this.#clearResumePending()
      return
    }
    this.#resumePending = true
    try {
      video.currentTime = t
    } catch (e) {}
    this.#resumeTries += 1
    if (this.#resumeTries >= 8) {
      this.#clearResumePending()
      return
    }
    if (this.#resumeTimer) clearTimeout(this.#resumeTimer)
    this.#resumeTimer = setTimeout(() => this.#tryApplyResume(), 400)
  }

  #clearResumePending () {
    this.#resumePending = false
    this.#resumeDone = true
    if (this.#resumeTimer) {
      clearTimeout(this.#resumeTimer)
      this.#resumeTimer = null
    }
  }

  #loadHls () {
    // 仅在客户端动态加载 hls.js，避免 SSR 时拉入浏览器库
    if (typeof window === 'undefined') {
      return Promise.reject(new Error('hls.js only available on client'))
    }
    if (window.Hls) {
      return Promise.resolve(window.Hls)
    }
    return import('hls.js').then((mod) => {
      const Hls = mod.default || mod
      if (!Hls || !Hls.isSupported()) {
        throw new Error('hls.js unavailable or not supported')
      }
      return Hls
    })
  }

  #destroyHls () {
    if (!this.#hls) return
    try { this.#hls.destroy() } catch (e) {}
    this.#hls = null
  }

  #emit (key, ...args) {
    const cbs = this.event[key]
    if (cbs && cbs.length) {
      cbs.forEach((fn) => fn && fn(...args))
    }
  }

  setMaxSeek (sec) {
    this.#maxSeek = Math.max(this.#maxSeek, Number(sec) || 0)
  }

  getMaxSeek () {
    return Math.max(this.#maxSeek, 0)
  }

  pause () {
    this.#holdPause = true
    this.playStatus = false
    if (this.video) {
      try {
        this.video.pause()
      } catch (e) {}
    }
  }

  play () {
    if (!this.video) return
    this.#holdPause = false
    this.video.playbackRate = 1
    const p = this.video.play()
    if (p && typeof p.catch === 'function') {
      p.catch(() => {})
    }
  }

  seek (s = 0) {
    if (!this.video) return
    let t = Number(s) || 0
    if (this.#banSeek) {
      const max = Math.max(this.#maxSeek, 0)
      // 已学区间内可前进/后退；超过已学最大则打回
      if (t > max + 0.25) {
        t = max
        this.#emit('seek_blocked', t)
      }
    }
    try {
      this.video.currentTime = t
    } catch (e) {}
  }

  destroy () {
    this.#clearResumePending()
    this.#destroyHls()
    clearInterval(this.#timeupdateTimer)
    clearInterval(this.#rateGuardTimer)
    clearInterval(this.#controlsGuardTimer)
    if (this.#seekSettleTimer) {
      clearTimeout(this.#seekSettleTimer)
      this.#seekSettleTimer = null
    }
    this.#timeupdateTimer = null
    this.#rateGuardTimer = null
    this.#controlsGuardTimer = null
    if (this.#controlsObserver) {
      try {
        this.#controlsObserver.disconnect()
      } catch (e) {}
      this.#controlsObserver = null
    }
    if (this.#fsGuard) {
      const { onWebkitBegin, onWebkitEnd, onFsChange, onPageShow, onVisibility, video, exitContainerFs } = this.#fsGuard
      try {
        if (typeof exitContainerFs === 'function') exitContainerFs()
      } catch (e) {}
      try {
        if (video) {
          video.removeEventListener('webkitbeginfullscreen', onWebkitBegin)
          if (onWebkitEnd) video.removeEventListener('webkitendfullscreen', onWebkitEnd)
        }
      } catch (e) {}
      document.removeEventListener('fullscreenchange', onFsChange)
      document.removeEventListener('webkitfullscreenchange', onFsChange)
      document.removeEventListener('mozfullscreenchange', onFsChange)
      document.removeEventListener('MSFullscreenChange', onFsChange)
      if (onPageShow) window.removeEventListener('pageshow', onPageShow)
      if (onVisibility) document.removeEventListener('visibilitychange', onVisibility)
      this.#fsGuard = null
    }
    if (this.#cssLandscapeFs && this.el) {
      try {
        this.#exitCssLandscapeFs(this.el)
      } catch (e) {}
    }
    this.#removeVideoShield()
    if (this.#onFsViewportChange) {
      window.removeEventListener('resize', this.#onFsViewportChange)
      window.removeEventListener('orientationchange', this.#onFsViewportChange)
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.#onFsViewportChange)
        window.visualViewport.removeEventListener('scroll', this.#onFsViewportChange)
      }
      this.#onFsViewportChange = null
    }
    if (this.#ui?.onMove) {
      window.removeEventListener('mousemove', this.#ui.onMove)
    }
    if (this.#ui?.onUp) {
      window.removeEventListener('mouseup', this.#ui.onUp)
    }
    if (this.#onVisibility) {
      document.removeEventListener('visibilitychange', this.#onVisibility)
      this.#onVisibility = null
    }
    if (this.#onWindowBlur) {
      window.removeEventListener('blur', this.#onWindowBlur)
      this.#onWindowBlur = null
    }
    if (this.video) {
      this.video.pause()
      this.video.removeAttribute('src')
      this.video.load()
      this.video.remove()
      this.video = null
    }
    if (this.el) {
      this.el.innerHTML = ''
    }
    this.#ui = null
    this.event = {}
  }

  getRealPlayVideoTime () {
    return this.getCurrentTime()
  }

  getCurrentTime () {
    return this.video ? (this.video.currentTime || 0) : 0
  }

  getDuration () {
    if (!this.video) return 0
    const d = this.video.duration
    return Number.isFinite(d) ? d : 0
  }

  getPercentage () {
    const time = this.getCurrentTime()
    const d = this.getDuration()
    if (!d) return 0
    if (d - time < 5) return 100
    return parseFloat(((time / d) * 100).toFixed(2))
  }

  getPlayer () {
    return this.video
  }

  on (key = '', fn = () => {}) {
    const callbacks = this.event[key] || []
    callbacks.push(fn)
    this.event[key] = callbacks
    return this
  }

  off (key = '', fn = () => {}) {
    const callbacks = this.event[key]
    this.event[key] = callbacks && callbacks.filter((cb) => cb !== fn)
    return this
  }
}

export default UrlPlayer

